import { format, subDays } from "date-fns";

// Email campaign data structure
export interface EmailCampaign {
  id: string;
  subject: string;
  emailType: "newsletter" | "press_release" | "bulletin" | "eblast";
  sentDate: string;
  recipientCount: number;
  openRate: number;
  clickRate: number;
  status: "sent" | "draft" | "scheduled";
  fromName: string;
  fromEmail: string;
}

/**
 * Generates fake email campaign data for testing and development
 * @param count Number of email campaigns to generate (defaults to 30)
 * @returns Array of EmailCampaign objects
 */
export const generateFakeEmailData = (count: number = 30): EmailCampaign[] => {
  const emailSubjects = [
    "Chamber Newsletter - May Edition",
    "Upcoming Business Forum - RSVP Today",
    "Meet Your New Chamber Members",
    "Small Business Spotlight: Downtown Revitalization",
    "Important: Chamber Office Holiday Closure",
    "Summer Networking Series Announced",
    "Chamber Perks: New Member Benefits",
    "Economic Development Update - Q2",
    "Annual Golf Tournament - Registration Open",
    "Business After Hours - This Thursday",
    "Chamber Board Election Results",
    "Leadership Program Applications Due",
    "COVID-19 Business Recovery Resources",
    "Legislative Update: New Tax Incentives",
    "Job Fair: Connect with Local Talent",
    "Chamber Survey - We Value Your Input",
    "Congratulations to Award Winners",
    "Workshop: Digital Marketing Strategies",
    "Annual Business Summit - Early Bird Pricing",
    "Special Announcement: New Chamber Initiative",
  ];

  const emailTypes = ["newsletter", "press_release", "bulletin", "eblast"];
  const fromNames = [
    "Chamber Team",
    "Chamber President",
    "Events Committee",
    "Membership Director",
  ];
  const domains = [
    "chamber.org",
    "chambercommerce.org",
    "business-chamber.org",
  ];

  return Array.from({ length: count }, (_, i) => {
    const emailType = emailTypes[
      Math.floor(Math.random() * emailTypes.length)
    ] as EmailCampaign["emailType"];
    const fromName = fromNames[Math.floor(Math.random() * fromNames.length)];
    const domain = domains[Math.floor(Math.random() * domains.length)];
    const fromEmail = `${fromName.toLowerCase().replace(/\s/g, ".")}@${domain}`;

    // Most emails will be sent, fewer are drafts
    const statusRandom = Math.random();
    let status: EmailCampaign["status"] = "sent";
    if (statusRandom > 0.9) {
      status = "draft";
    } else if (statusRandom > 0.85) {
      status = "scheduled";
    }

    // Generate a random sent date within the last 90 days
    const dayOffset = Math.floor(Math.random() * 90);
    const sentDate =
      status === "draft"
        ? ""
        : format(subDays(new Date(), dayOffset), "MMM dd, yyyy");

    // Generate open and click rates - better for newsletters, worse for other types
    let openRateBase = emailType === "newsletter" ? 35 : 25;
    let clickRateBase = emailType === "newsletter" ? 9 : 6;

    // Add some variation
    const openRateVariation = Math.floor(Math.random() * 15) - 5; // -5 to +10
    const clickRateVariation = Math.floor(Math.random() * 6) - 2; // -2 to +4

    const openRate = Math.max(
      10,
      Math.min(70, openRateBase + openRateVariation)
    );
    const clickRate = Math.max(
      2,
      Math.min(20, clickRateBase + clickRateVariation)
    );

    // Recipients between a few hundred and a few thousand
    const recipientCount = Math.floor(Math.random() * 2000) + 300;

    return {
      id: `email-${i + 1}`,
      subject: emailSubjects[i % emailSubjects.length],
      emailType,
      sentDate,
      recipientCount,
      openRate,
      clickRate,
      status,
      fromName,
      fromEmail,
    };
  });
};
